// src/components/albums/AddToAlbumModal.js

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import toast from 'react-hot-toast';

// 🌟 UPGRADED: Destructuring isDarkMode to control context changes cleanly
const AddToAlbumModal = ({ isOpen, onClose, album, onSongAdded, isDarkMode }) => {
  const [discography, setDiscography] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Effect to fetch the artist's entire discography
  useEffect(() => {
    if (!isOpen || !album?.artistId) return;

    const fetchDiscography = async () => {
      setIsLoading(true);
      const db = getFirestore();
      const q = query(
        collection(db, 'songs'), 
        where('artistId', '==', album.artistId),
        where('isDeleted', '!=', true) // 🌟 FIX: Instantly drops deleted items from the pool
      );
      const snapshot = await getDocs(q);
      const allSongs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDiscography(allSongs);
      setIsLoading(false);
    };

    fetchDiscography();
  }, [isOpen, album?.artistId]);

  // Filter songs based on search term and if they're already in the album
  const filteredSongs = useMemo(() => {
    return discography.filter(song => {
      const isAlreadyInAlbum = album.songIds?.includes(song.id);
      const matchesSearch = song.title.toLowerCase().includes(searchTerm.toLowerCase());
      return !isAlreadyInAlbum && matchesSearch;
    });
  }, [searchTerm, discography, album?.songIds]);

  const handleAddSong = async (songToAdd) => {
    const db = getFirestore();
    const albumRef = doc(db, 'albums', album.id);
    try {
      await updateDoc(albumRef, { songIds: arrayUnion(songToAdd.id) });
      toast.success(`Added "${songToAdd.title}"`);
      onSongAdded(); // This will refresh the song list on the detail page
    } catch (error) {
      toast.error("Failed to add song.");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm" 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          onClick={onClose}
        >
          {/* 🌟 UPGRADED: Unified Light/Dark Modal Base Chassis Container layout */}
          <motion.div 
            className={`rounded-2xl shadow-2xl w-full max-w-sm flex flex-col border overflow-hidden transition-all duration-300 ${
              isDarkMode 
                ? "bg-zinc-900 border-zinc-800 text-white shadow-black/60" 
                : "bg-white border-slate-100 text-slate-900 shadow-slate-200"
            }`} 
            style={{ height: '70vh' }} 
            initial={{ scale: 0.9, y: 15 }} 
            animate={{ scale: 1, y: 0 }} 
            exit={{ scale: 0.9, opacity: 0, y: 15 }}
            transition={{ type: "spring", duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Search Parameters Input Section */}
            <div className={`p-4 border-b ${isDarkMode ? 'border-zinc-800 bg-zinc-950/20' : 'border-slate-100 bg-slate-50/50'}`}>
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-black uppercase tracking-wider italic">
                  Add Song to Album
                </h2>
                <button 
                  onClick={onClose} 
                  className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded transition-colors ${
                    isDarkMode ? 'text-zinc-500 hover:text-white' : 'text-slate-400 hover:text-slate-700'
                  }`}
                >
                  Done
                </button>
              </div>
              
              {/* 🌟 UPGRADED: Dynamic track name filtering search bar */}
              <input 
                type="text" 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                placeholder="Search your songs..." 
                className={`w-full p-2.5 text-xs font-semibold rounded-xl border mt-2.5 transition-all focus:outline-none focus:border-brand-pink/50 ${
                  isDarkMode 
                    ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                    : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                }`} 
              />
            </div>
            
            {/* Scroll Containment Stream Container for available tracks */}
            <div className="flex-grow overflow-y-auto custom-scrollbar">
              {isLoading ? (
                <p className={`text-center text-xs font-bold uppercase tracking-widest py-8 ${isDarkMode ? "text-zinc-600 animate-pulse" : "text-gray-400 animate-pulse"}`}>
                  Querying Ledger...
                </p>
              ) : (
                <ul className="divide-y divide-transparent space-y-0.5 p-2">
                  {filteredSongs.length > 0 ? filteredSongs.map(song => (
                    // 🌟 UPGRADED: Responsive dark mode tracking lists rows items with safe hover highlights
                    <li 
                      key={song.id} 
                      className={`flex items-center p-2.5 rounded-xl transition-colors ${
                        isDarkMode ? "hover:bg-zinc-950/60" : "hover:bg-gray-50"
                      }`}
                    >
                      <img src={song.albumArtworkURL} alt={song.title} className="w-12 h-12 rounded-lg object-cover mr-3 shadow-md flex-shrink-0" />
                      <div className="flex-grow min-w-0 pr-2">
                        <p className="font-bold text-sm tracking-tight truncate">{song.title}</p>
                        <p className={`text-[11px] font-medium truncate ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>{song.artistName}</p>
                      </div>
                      
                      {/* 🌟 UPGRADED: Transformed legacy blue click triggers into clean branding elements */}
                      <button 
                        onClick={() => handleAddSong(song)} 
                        className={`w-7 h-7 flex items-center justify-center text-sm font-black rounded-full shadow-sm transition-all active:scale-90 focus:outline-none ${
                          isDarkMode 
                            ? "bg-brand-pink/10 border border-brand-pink/30 text-brand-pink hover:bg-brand-pink hover:text-white shadow-black/20" 
                            : "bg-pink-50 border border-pink-100 text-pink-600 hover:bg-pink-600 hover:text-white shadow-pink-100"
                        }`}
                      >
                        +
                      </button>
                    </li>
                  )) : (
                    <p className={`text-center text-xs font-semibold py-8 ${isDarkMode ? "text-zinc-500" : "text-gray-400"}`}>
                      {searchTerm ? 'No tracks found matching query.' : 'All discography items are inside the album.'}
                    </p>
                  )}
                </ul>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddToAlbumModal;